// src/app/api/parentApi.ts
//
// RTK Query slice for all /api/parent/* endpoints.
// Every type here is derived directly from the response shapes in
// routes/parent.js — field names, optionality, and nesting are exact.

import { baseApi } from "./baseApi";
import type { StudentTrack, StudentStatus } from "./authApi";

/* ─────────────────────────────────────────────────────────────────
   SHARED PRIMITIVES
───────────────────────────────────────────────────────────────── */

export type Quarter = "Q1" | "Q2" | "Q3";
export type EnrolStage = "Stage1" | "Stage2" | "Stage3" | "Stage4" | null;
export type PayStatus =
  | "pending"
  | "paid"
  | "overdue"
  | "waived"
  | "failed"
  | "refunded";
export type EnrolStatus = "pending" | "confirmed" | "completed" | "withdrawn";
export type PayMethod =
  | "paystack"
  | "bank_transfer"
  | "cash"
  | "waiver"
  | "other";
export type PayType = "enrolment_fee" | "material_fee" | "exam_fee" | "other";
export type MessageDir = "inbound" | "outbound";
export type ShowcaseFormat =
  | "classroom_showcase"
  | "game_demo_day"
  | "innovation_showcase"
  | "portfolio_showcase"
  | "deployed_website_showcase"
  | "innovation_pitch"
  | "portfolio_presentation"
  | "fullstack_demo_day"
  | "three_platform_demo"
  | "grand_showcase"
  | "other";

/* ─────────────────────────────────────────────────────────────────
   CHILD CARD
   Shape returned inside dashboard.children[] and /children list.
───────────────────────────────────────────────────────────────── */

export interface ChildCard {
  id: string;
  firstName: string;
  lastName: string;
  name: string; // "firstName lastName"
  age: number;
  avatar: string | null;
  track: StudentTrack;
  studentStatus: StudentStatus;
  // Enrolment-level fields (null when not yet enrolled)
  quarter: Quarter | null;
  week: number | null;
  nextSession: string | null; // human label e.g. "Sat 31 May, 10:00 AM"
  nextSessionAt: string | null; // ISO datetime
  progress: number; // 0–100
  currentProject: string | null;
  paymentStatus: PayStatus;
  enrolmentId: string | null;
  enrolmentStatus: EnrolStatus | null;
  billingPeriod: "monthly" | "quarterly" | "annually" | null;
  billingLabel: string | null;
  nextPaymentDate: string | null;
  nextPaymentLabel: string | null;
}

/* ─────────────────────────────────────────────────────────────────
   CHILD DETAIL  (GET /children/:id)
───────────────────────────────────────────────────────────────── */

export interface ChildDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  age: number;
  dateOfBirth: string;
  track: StudentTrack;
  studentStatus: StudentStatus;
  avatar: string | null;
  enrolments: Enrolment[];
  payments: PaymentSummary[];
  createdAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   ENROLMENT
───────────────────────────────────────────────────────────────── */

export interface EnrolmentChild {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  track: StudentTrack;
  studentStatus: StudentStatus;
  avatar: string | null;
}

export interface Enrolment {
  id: string;
  child: EnrolmentChild | null;
  parent?: string;
  academicYear: string;
  quarter: Quarter;
  track: StudentTrack;
  stage: EnrolStage;
  // Billing period & pricing
  billingPeriod: "monthly" | "quarterly" | "annually" | null;
  billingLabel: string | null;
  monthsCovered: 1 | 3 | 12 | null;
  baseMonthlyFee: number | null;
  grossAmount: number | null;
  discountPercent: number;
  discountAmount: number;
  familyDiscountPercent?: number;
  familyDiscountAmount?: number;
  feeAmount: number;
  currency: string;
  // Next payment date
  nextPaymentDate: string | null;
  nextPaymentLabel: string | null;
  // Progress
  currentWeek: number;
  progressPercent: number;
  currentProject: string | null;
  nextSessionAt: string | null;
  nextSessionLabel: string | null;
  // Payment state
  paymentStatus: PayStatus;
  paidAt: string | null;
  status: EnrolStatus;
  confirmedAt: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
  // Only present on detail endpoint
  payments?: PaymentSummary[];
}

/* ─────────────────────────────────────────────────────────────────
   PAYMENT
   PaymentSummary = the dashboard / list shape from formatPayment()
   PaymentDetail  = the GET /payments/:id shape
───────────────────────────────────────────────────────────────── */

export interface PaymentEnrolmentContext {
  id: string;
  quarter: string;
  academicYear: string;
  billingPeriod: "monthly" | "quarterly" | "annually" | null;
  monthsCovered: 1 | 3 | 12 | null;
  billingLabel: string | null; // "Monthly" | "Quarterly" | "Annual"
  grossAmount: number | null;
  discountPercent: number;
  discountAmount: number;
  familyDiscountPercent?: number;
  familyDiscountAmount?: number;
  feeAmount: number | null;
  track: StudentTrack;
  status: string;
}

export interface PaymentSummary {
  id: string;
  desc: string;
  amount: string; // formatted e.g. "₦22,000"
  amountRaw: number;
  currency: string;
  date: string; // formatted display date
  status: PayStatus;
  method: string | null;
  reference: string | null;
  receiptUrl: string | null;
  child: { id: string; firstName: string; lastName: string } | null;
  enrolment: PaymentEnrolmentContext | string; // populated object or ObjectId string
  nextPaymentDate: string | null; // ISO datetime of next renewal
  nextPaymentLabel: string | null; // e.g. "27 Jun 2026"
  paidAt: string | null;
  createdAt: string;
  /** The 14-day money-back guarantee on a child's first payment */
  moneyBack?: MoneyBack;
}

export interface MoneyBack {
  eligible: boolean;
  until: string | null;
  reason: string | null;
  request: {
    status: "requested" | "approved" | "declined";
    requestedAt: string;
    reason: string | null;
    decidedAt: string | null;
    decidedByName: string | null;
    note: string | null;
  } | null;
}

export interface PaymentDetail {
  id: string;
  description: string;
  type: PayType;
  amount: number;
  amountFormatted: string;
  currency: string;
  status: PayStatus;
  method: PayMethod;
  reference: string | null;
  gatewayResponse: string | null;
  dueDate: string | null;
  paidAt: string | null;
  receiptUrl: string | null;
  notes: string | null;
  child: {
    id: string;
    firstName: string;
    lastName: string;
    age: number;
    track: StudentTrack;
  } | null;
  enrolment: {
    id: string;
    quarter: Quarter;
    academicYear: string;
    track: StudentTrack;
    status: EnrolStatus;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentInitiateResult {
  paymentId: string;
  reference: string;
  paystackData: {
    email: string;
    amount: number; // in kobo
    reference: string;
    currency: string;
    metadata: {
      paymentId: string;
      enrolmentId: string;
      childName: string;
      quarter: Quarter;
      parentId: string;
    };
    callback_url: string;
  };
}

/* ─────────────────────────────────────────────────────────────────
   SHOWCASE
───────────────────────────────────────────────────────────────── */

export interface ShowcaseSummary {
  id: string;
  name: string;
  date: string; // formatted display date
  daysLeft: number;
  track: StudentTrack | "all";
  format: ShowcaseFormat;
  location: string | null;
  parentsInvited: boolean;
  // List endpoint only
  description?: string | null;
  durationMinutes?: number;
  externalJudges?: boolean;
  hasParticipant?: boolean;
}

export interface ShowcaseDetail extends ShowcaseSummary {
  quarter: Quarter | null;
  stage: EnrolStage;
  academicYear: string;
  scheduledAt: string;
  dateLabel: string | null;
  participatingChildren: {
    id: string;
    firstName: string;
    lastName: string;
    track: StudentTrack;
  }[];
}

/* ─────────────────────────────────────────────────────────────────
   MESSAGE
───────────────────────────────────────────────────────────────── */

export interface MessagePreview {
  id: string;
  threadId: string;
  from: string; // senderName
  senderRole: string;
  subject: string;
  unread: boolean;
  time: string; // human-relative time
  child: string | null; // child ObjectId
  createdAt: string;
  // Inbox list only
  unreadCount?: number;
  totalMessages?: number;
}

export interface ThreadMessage {
  id: string;
  threadId: string;
  senderName: string;
  senderRole: string;
  direction: MessageDir;
  subject: string;
  body: string;
  readByParent: boolean;
  child: string | null;
  createdAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   DASHBOARD
───────────────────────────────────────────────────────────────── */

export interface DashboardStats {
  childrenCount: number;
  nextSession: string | null;
  upcomingShowcases: number;
  pendingAmount: number;
  unreadMessages: number;
}

export interface DashboardData {
  stats: DashboardStats;
  children: ChildCard[];
  payments: PaymentSummary[];
  showcases: ShowcaseSummary[];
  messages: MessagePreview[];
}

/* ─────────────────────────────────────────────────────────────────
   PROFILE
───────────────────────────────────────────────────────────────── */

/** State + city make a family's "cluster" — on-site opens cluster by cluster. */
export interface ParentLocation {
  state: string;
  city: string;
  area: string | null;
}

export interface ParentProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar: string | null;
  role: "parent";
  isEmailVerified: boolean;
  lastLoginAt: string | null;
  location: ParentLocation | null;
  onsiteInterest: { modes: ("hybrid" | "onsite")[]; registeredAt: string } | null;
  children: {
    id: string;
    firstName: string;
    lastName: string;
    age: number;
    track: StudentTrack;
    studentStatus: StudentStatus;
    avatar: string | null;
  }[];
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────────────
   REQUEST TYPES
───────────────────────────────────────────────────────────────── */

export interface GetPaymentsParams {
  childId?: string;
  status?: PayStatus;
  page?: number;
  limit?: number;
}

export interface GetEnrolmentsParams {
  childId?: string;
  quarter?: Quarter;
  academicYear?: string;
  status?: EnrolStatus;
}

export interface GetShowcasesParams {
  all?: boolean;
}

export interface SendMessageRequest {
  subject: string;
  body: string;
  childId?: string;
  threadId?: string; // omit for new thread
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatar?: string;
  location?: ParentLocation;
}

/* ─────────────────────────────────────────────────────────────────
   RESPONSE WRAPPERS
───────────────────────────────────────────────────────────────── */

interface ListResponse<T> {
  success: boolean;
  message: string;
  count: number;
  data: T[];
}

interface ListPagedResponse<T> extends ListResponse<T> {
  total: number;
  page: number;
  pages: number;
}

interface SingleResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/* ─────────────────────────────────────────────────────────────────
   API SLICE
───────────────────────────────────────────────────────────────── */

export const parentApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── DASHBOARD ───────────────────────────────────────────────
    /**
     * Single call that returns the full parent dashboard payload.
     * Used by ParentDashboard as the primary data source.
     */
    getParentDashboard: builder.query<SingleResponse<DashboardData>, void>({
      query: () => "/parent/dashboard",
      providesTags: [
        "ParentDashboard" as const,
        "ParentChildren" as const,
        "ParentPayments" as const,
        "ParentShowcases" as const,
        "ParentMessages" as const,
        "ParentProfile" as const,
      ],
    }),

    // ── CHILDREN ─────────────────────────────────────────────────
    getChildren: builder.query<ListResponse<ChildCard>, void>({
      query: () => "/parent/children",
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ParentChildren" as const,
                id,
              })),
              { type: "ParentChildren" as const, id: "LIST" },
            ]
          : [{ type: "ParentChildren" as const, id: "LIST" }],
    }),

    getChild: builder.query<SingleResponse<ChildDetail>, string>({
      query: (id) => `/parent/children/${id}`,
      providesTags: (_result, _err, id) => [
        { type: "ParentChildren" as const, id },
      ],
    }),

    updateChild: builder.mutation<
      SingleResponse<ChildDetail>,
      { id: string; avatar?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/parent/children/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { id }) => [
        { type: "ParentChildren" as const, id },
        { type: "ParentChildren" as const, id: "LIST" },
        "ParentDashboard" as const,
      ],
    }),

    // ── ENROLMENTS ───────────────────────────────────────────────
    getEnrolments: builder.query<ListResponse<Enrolment>, GetEnrolmentsParams>({
      query: (params = {}) => ({
        url: "/parent/enrolments",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ParentEnrolments" as const,
                id,
              })),
              { type: "ParentEnrolments" as const, id: "LIST" },
            ]
          : [{ type: "ParentEnrolments" as const, id: "LIST" }],
    }),

    getEnrolment: builder.query<SingleResponse<Enrolment>, string>({
      query: (id) => `/parent/enrolments/${id}`,
      providesTags: (_result, _err, id) => [
        { type: "ParentEnrolments" as const, id },
      ],
    }),

    // ── PAYMENTS ─────────────────────────────────────────────────
    getPayments: builder.query<
      ListPagedResponse<PaymentSummary>,
      GetPaymentsParams
    >({
      query: (params = {}) => ({
        url: "/parent/payments",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ParentPayments" as const,
                id,
              })),
              { type: "ParentPayments" as const, id: "LIST" },
            ]
          : [{ type: "ParentPayments" as const, id: "LIST" }],
    }),

    getPayment: builder.query<SingleResponse<PaymentDetail>, string>({
      query: (id) => `/parent/payments/${id}`,
      providesTags: (_result, _err, id) => [
        { type: "ParentPayments" as const, id },
      ],
    }),

    requestRefund: builder.mutation<SingleResponse<{ moneyBack: MoneyBack }>, { id: string; reason?: string }>({
      query: ({ id, reason }) => ({ url: `/parent/payments/${id}/refund-request`, method: "POST", body: { reason } }),
      invalidatesTags: [{ type: "ParentPayments" as const, id: "LIST" }],
    }),

    initiatePayment: builder.mutation<
      SingleResponse<PaymentInitiateResult>,
      { enrolmentId: string }
    >({
      query: (body) => ({
        url: "/parent/payments/initiate",
        method: "POST",
        body,
      }),
      // Invalidate payments + dashboard so the pending payment shows up immediately
      invalidatesTags: [
        { type: "ParentPayments" as const, id: "LIST" },
        "ParentDashboard" as const,
      ],
    }),

    // ── SHOWCASES ────────────────────────────────────────────────
    getShowcases: builder.query<
      ListResponse<ShowcaseSummary>,
      GetShowcasesParams
    >({
      query: ({ all } = {}) => ({
        url: "/parent/showcases",
        params: all ? { all: "true" } : {},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: "ParentShowcases" as const,
                id,
              })),
              { type: "ParentShowcases" as const, id: "LIST" },
            ]
          : [{ type: "ParentShowcases" as const, id: "LIST" }],
    }),

    getShowcase: builder.query<SingleResponse<ShowcaseDetail>, string>({
      query: (id) => `/parent/showcases/${id}`,
      providesTags: (_result, _err, id) => [
        { type: "ParentShowcases" as const, id },
      ],
    }),

    // ── MESSAGES ─────────────────────────────────────────────────
    getMessages: builder.query<ListResponse<MessagePreview>, void>({
      query: () => "/parent/messages",
      providesTags: [{ type: "ParentMessages" as const, id: "LIST" }],
    }),

    getUnreadCount: builder.query<
      SingleResponse<{ unreadCount: number }>,
      void
    >({
      query: () => "/parent/messages/unread-count",
      providesTags: [{ type: "ParentMessages" as const, id: "UNREAD" }],
    }),

    getThread: builder.query<ListResponse<ThreadMessage>, string>({
      query: (threadId) => `/parent/messages/thread/${threadId}`,
      // Reading a thread marks messages as read — invalidate the inbox + unread count
      onQueryStarted: async (_threadId, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(
          parentApi.util.invalidateTags([
            { type: "ParentMessages" as const, id: "LIST" },
            { type: "ParentMessages" as const, id: "UNREAD" },
            "ParentDashboard" as const,
          ]),
        );
      },
    }),

    sendMessage: builder.mutation<
      SingleResponse<ThreadMessage>,
      SendMessageRequest
    >({
      query: (body) => ({
        url: "/parent/messages",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "ParentMessages" as const, id: "LIST" },
        "ParentDashboard" as const,
      ],
    }),

    markMessageRead: builder.mutation<SingleResponse<ThreadMessage>, string>({
      query: (id) => ({
        url: `/parent/messages/${id}/read`,
        method: "PATCH",
      }),
      invalidatesTags: [
        { type: "ParentMessages" as const, id: "LIST" },
        { type: "ParentMessages" as const, id: "UNREAD" },
        "ParentDashboard" as const,
      ],
    }),

    // ── PROFILE ──────────────────────────────────────────────────
    getParentProfile: builder.query<SingleResponse<ParentProfile>, void>({
      query: () => "/parent/profile",
      providesTags: ["ParentProfile" as const],
    }),

    updateParentProfile: builder.mutation<
      SingleResponse<
        Pick<
          ParentProfile,
          "id" | "firstName" | "lastName" | "email" | "avatar" | "updatedAt"
        >
      >,
      UpdateProfileRequest
    >({
      query: (body) => ({
        url: "/parent/profile",
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["ParentProfile" as const, "ParentDashboard" as const],
    }),

    // ── HYBRID / ON-SITE INTEREST ("coming soon") ────────────────
    registerOnsiteInterest: builder.mutation<
      SingleResponse<{
        location: ParentLocation | null;
        onsiteInterest: { modes: ("hybrid" | "onsite")[]; registeredAt: string } | null;
      }>,
      { modes: ("hybrid" | "onsite")[]; location?: ParentLocation }
    >({
      query: (body) => ({
        url: "/parent/onsite-interest",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ParentProfile" as const],
    }),
  }),
});

/* ─────────────────────────────────────────────────────────────────
   EXPORTED HOOKS
───────────────────────────────────────────────────────────────── */

export const {
  // Dashboard
  useGetParentDashboardQuery,

  // Children
  useGetChildrenQuery,
  useGetChildQuery,
  useUpdateChildMutation,

  // Enrolments
  useGetEnrolmentsQuery,
  useGetEnrolmentQuery,

  // Payments
  useGetPaymentsQuery,
  useGetPaymentQuery,
  useInitiatePaymentMutation,
  useRequestRefundMutation,

  // Showcases
  useGetShowcasesQuery,
  useGetShowcaseQuery,

  // Messages
  useGetMessagesQuery,
  useGetUnreadCountQuery,
  useGetThreadQuery,
  useSendMessageMutation,
  useMarkMessageReadMutation,

  // Profile
  useGetParentProfileQuery,
  useUpdateParentProfileMutation,
  useRegisterOnsiteInterestMutation,
} = parentApi;

// Convenience aliases so UserSettings can import with shorter names
export const useGetProfileQuery = useGetParentProfileQuery;
export const useUpdateProfileMutation = useUpdateParentProfileMutation;
